import { useState, useEffect } from "react";
import {
  Box,
  Checkbox,
  Container,
  CircularProgress,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
} from "@mui/material";

import { Link, useNavigate } from "react-router-dom";
import useSWRMutation from "swr/mutation";

import { useClient } from "../../context/ClientProvider";
import { useUtils } from "../../context/UtilsProvider";
import { useAuth } from "../../context/AuthProvider";

import { AccountReference, validateAccountReference } from "./input/AccountReference";
import { Postcode, validatePostcode } from "./input/Postcode";
import { DateOfBirth, validateDateOfBirth } from "./input/DateOfBirth";

export default function IdvLoginCard() {

  const { useGetConfig } = useClient();
  const { clientConfig } = useGetConfig();

  const {
    formState,
    setFormState,
    formError,
    setFormError,
    createAlert,
    alertSeverityTypes,
  } = useUtils();

  const navigate = useNavigate();

  const { postIdvAuth, startSession } = useAuth();
  const { trigger, isMutating } = useSWRMutation("/auth/idv", postIdvAuth);

  const [checked, setChecked] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setButtonDisabled(true);
    let errorMessage = null;
    try {
      if (!validateAccountReference(formState.accountReference)) {
        errorMessage = "Invalid account reference number";
        throw new Error(errorMessage);
      };
      if (!validatePostcode(formState.postcode)) {
        errorMessage = "Invalid postcode";
        throw new Error(errorMessage);
      };
      if (!validateDateOfBirth(formState.dateOfBirth)) {
        errorMessage = "Invalid date of birth";
        throw new Error(errorMessage);
      };
      const response = await trigger({
        ...formState,
        dateOfBirth: formState.dateOfBirth,
        postcode: formState.postcode.toUpperCase(),
      });
      console.info("post idv auth response: ", response);
      setFormState(null);
      startSession(response.authenticationResult);
      createAlert("IDV authentication successful", alertSeverityTypes.success);
      navigate("/");
    } catch (error) {
      if (!errorMessage) {
        errorMessage = "Hmm... something's not right. Please try again";
      };
      console.error("post idv auth error", errorMessage);
      setFormError(errorMessage);
      createAlert(errorMessage, alertSeverityTypes.error);
      setButtonDisabled(false);
    }
  };

  useEffect(() => {
    if (
      formState.accountReference &&
      formState.dateOfBirth &&
      formState.postcode
    ) {
      setButtonDisabled(false);
    } else {
      setButtonDisabled(true);
    }
  }, [formState]);

  return (
    clientConfig && (
      <Card
        sx={{
          maxWidth: { md: "80%" },
          margin: "0px auto",
          borderRadius: "0",
          position: "relative",
          bottom: { xs: "auto", md: "300px" },
          boxShadow: "-9px 10px 4px -1px rgba(0, 0, 0, 0.2)",
        }}
      >
        <Container
          sx={{
            marginTop: "25px",
          }}
        >
          <Typography
            component='h1'
            variant='h5'
            fontWeight={"bold"}
            marginBottom={1}
          >
            {clientConfig.layout.auth.login.headerText}
          </Typography>
          <Typography component='h2' variant='p'>
            {clientConfig.layout.auth.login.subHeaderText}
          </Typography>
        </Container>

        <Box
          component='form'
          onSubmit={handleSubmit}
          noValidate
          sx={{ margin: "0px" }}
        >
          <Stack
            sx={{
              paddingX: 1,
            }}
            direction={{
              xs: "column",
              md: "row",
            }}
          >
            <CardContent
              sx={{
                textAlign: "left",
                width: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Account reference input */}
              <Box id="accountReference">
                <AccountReference
                  label={`${clientConfig?.name} reference number`}
                  inputLabel={`Enter your ${clientConfig?.name} reference number`}
                  formState={formState}
                  setFormState={setFormState}
                  setFormError={setFormError}
                />
              </Box>
              {/* End of account reference input */}

              {/* Postcode input */}
              <Box id="postcode" sx={{ mt: { xs: 4, md: 2 } }}>
                <Postcode
                  label="Postcode"
                  inputLabel="Enter your postcode"
                  formState={formState}
                  setFormState={setFormState}
                  setFormError={setFormError}
                />
              </Box>
              {/* End of postcode input */}
            </CardContent>

            <CardContent
              sx={{
                textAlign: "left",
                width: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <DateOfBirth
                label="Date of birth"
                formState={formState}
                setFormState={setFormState}
                setFormError={setFormError}
              />
            </CardContent>
          </Stack>

          <Container
            sx={{
              marginBottom: "10px",
            }}
          >
            <Checkbox
              checked={checked}
              onChange={() => {
                setChecked(!checked);
              }}
              inputProps={{ "aria-label": "controlled" }}
              sx={{
                marginRight: { xs: "8px", sm: 0 },
                marginBottom: { xs: "8px", sm: 0 },
              }}
            />
            <Typography
              component='h2'
              variant='p'
              sx={{
                display: "inline-block",
                verticalAlign: {
                  xs: "middle",
                  sm: "middle",
                  md: "baseline",
                  lg: "baseline",
                },
                maxWidth: { xs: "88%" },
                marginLeft: { xs: "0", sm: "8px" },
              }}
            >
              Tick this box to agree to confirm you've read & accepted our{" "}
              <Link
                to={clientConfig?.layout?.links?.terms}
                style={{ color: "#007CCB" }}
                target='_blank'
                rel="noopener noreferrer"
              >
                Terms and Conditions
              </Link>{" "}
              &{" "}
              <Link
                to={clientConfig?.layout?.links?.privacy}
                style={{ color: "#007CCB" }}
                target='_blank'
                rel="noopener noreferrer"
              >
                Privacy Notice
              </Link>
            </Typography>
          </Container>

          <Container
            sx={{
              position: "relative",
              marginBottom: "20px",
              marginTop: "20px",
            }}
          >
            <Button
              type='submit'
              variant='contained'
              sx={{
                mb: 2,
                marginLeft: "1%",
                borderRadius: clientConfig.layout.buttonBorderRadius,
                width: { xs: "60%", md: "15%" },
                backgroundColor: clientConfig.layout.primaryButtonBackgroundColour,
                color: clientConfig.layout.primaryButtonColour,
                "&:hover": {
                  backgroundColor: clientConfig.layout.hoverPrimaryButtonBackgroundColour,
                  color: clientConfig.layout.hoverPrimaryButtonColour,
                }
              }}
              disabled={
                !(
                  checked &&
                  formState.accountReference &&
                  formState.dateOfBirth &&
                  formState.postcode &&
                  !buttonDisabled
                )
              }
            >
              Login
            </Button>
            {isMutating && (
              <CircularProgress
                size={24}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: "-12px",
                  marginLeft: "-12px",
                }}
              />
            )}
            <Typography
              sx={{ ml: 2, display: "inline-block", mb: { xs: 2 } }}
              color='error'
            >
              {formError && `* ${formError}`}
            </Typography>
          </Container>
        </Box>
      </Card>
    )
  );
}
